/**
 * The shared broadcast menu frame (Channel Guide mock A8KMkP): painted stadium backdrop, top bar with the mini
 * logo + profile chips, a big Luckiest Guy header with a Bungee kicker, the body, button hints and an optional
 * HFN ticker along the bottom.
 */
import './menus.css';
import type { ComponentChildren } from 'preact';
import type { MenuAction } from '@hf/client-core/input/types';
import { Backdrop, CoinIcon, Hints, Logo, Ticker } from '../components';
import { walletBalance } from '../wallet';
import { openStore } from './store-nav';
import { PORTAL } from '../../portal';
import { rankTier, thousands } from '@hf/client-core/ui-model/format';
import { ui } from '@hf/client-core/ui-model/store';
import { useActions } from '../actions';
export { cycle } from '@hf/client-core/ui-model/screens';

export interface ShellProps {
  title: string;
  kicker?: string;
  screen: string;
  hints?: readonly [MenuAction | string, string][];
  ticker?: readonly ComponentChildren[];
  stadium?: 'day' | 'night';
  rays?: { a: string; b: string };
  class?: string;
  /** Clickable "◀ BACK" in the top bar (touch users have no Esc). */
  onBack?: () => void;
  children?: ComponentChildren;
}

export function ProfileChips() {
  const actions = useActions();
  const net = ui.net.value;
  const name = ui.settings.value.playerName.trim() || net.name || 'PLAYER 1';
  const rating = net.rating?.rating;
  return (
    <div class="hf-profile">
      {PORTAL ? null : (
        <button
          type="button"
          class="hf-chip gold hf-coin-chip interactive"
          data-testid="header-coins"
          aria-label={`${walletBalance.value} coins, open store`}
          onClick={() => openStore((s) => actions.goto(s), ui.screen.peek())}
        >
          <CoinIcon class="hf-coin-chip__icon" />
          {thousands(walletBalance.value)}
        </button>
      )}
      <span class="hf-chip red">P1 · {name.toUpperCase()}</span>
      {rating !== undefined ? (
        <span class="hf-chip purple">
          {rankTier(rating)} · {thousands(rating)}
        </span>
      ) : null}
      <span class={`hf-chip ${netOnline(net.status) ? 'lime' : 'dark'}`}>
        <span class={`hf-dot ${netOnline(net.status) ? 'on' : ''}`} aria-hidden="true" />
        {netOnline(net.status) ? 'ONLINE' : 'OFFLINE'}
      </span>
    </div>
  );
}

const netOnline = (s: string): boolean => s !== 'offline' && s !== 'error' && s !== 'connecting';

export function Shell(p: ShellProps) {
  return (
    <div class={`hf-screen hf-menu ${p.class ?? ''}`} data-screen={p.screen}>
      <Backdrop
        stadium={p.stadium ?? 'day'}
        rays={{ ...(p.rays ?? { a: '#2f2470', b: '#140c32' }), rays: 16, y: '-10%' }}
      />
      <header class="hf-menu__top">
        <div class="hf-menu__brand">
          {p.onBack ? (
            <button class="hf-back interactive" onClick={p.onBack} aria-label="Back">
              ◀
            </button>
          ) : null}
          <Logo size="sm" />
        </div>
        <ProfileChips />
      </header>
      <div class="hf-menu__head">
        <h1 class="hf-menu__title t-display">{p.title}</h1>
        {p.kicker ? <div class="hf-menu__kicker t-broadcast">{p.kicker}</div> : null}
      </div>
      <main class="hf-menu__body">{p.children}</main>
      {p.hints ? <Hints items={p.hints} class="hf-menu__hints" /> : null}
      {p.ticker ? <Ticker label="HFN SCORES" items={p.ticker} class="hf-menu__ticker" /> : null}
    </div>
  );
}

/** A labelled value stepper row (◀ value ▶) used by Mode Select, Settings and Practice. */
export function StepRow({
  label,
  value,
  hint,
  focused,
  disabled,
  onStep,
  item,
}: {
  label: string;
  value: ComponentChildren;
  hint?: string;
  focused: boolean;
  disabled?: boolean;
  onStep: (dir: -1 | 1) => void;
  item: Record<string, unknown>;
}) {
  return (
    <div
      class={`hf-row hf-steprow interactive ${disabled ? 'disabled' : ''}`}
      role="group"
      aria-label={label}
      {...item}
      data-focused={focused}
    >
      <div class="hf-row__label">
        <span class="t-display">{label}</span>
        {hint ? <small>{hint}</small> : null}
      </div>
      <div class="hf-steprow__ctl">
        <button
          class="hf-steprow__arrow"
          tabIndex={-1}
          aria-label={`Previous ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onStep(-1);
          }}
        >
          ◀
        </button>
        <span class="hf-steprow__value t-broadcast" aria-live="polite">
          {value}
        </span>
        <button
          class="hf-steprow__arrow"
          tabIndex={-1}
          aria-label={`Next ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onStep(1);
          }}
        >
          ▶
        </button>
      </div>
    </div>
  );
}
